import React, { useState, useRef, useCallback, useMemo } from 'react';
import { computeConsoleLayoutMeters } from './consoleLayoutGeometry';
import {
  Paper, Typography, Box, Grid, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Accordion, AccordionSummary, AccordionDetails, Tooltip, Divider
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { openConsolePrintReport } from '../../printReports/appPrintReports';
import { EurocodeText } from '../../utils/eurocodeNotation';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';

/** Bottle-shaped strut sketch: symmetric bulge (EC2 §6.5.2 transverse tension), screen px. */
function bottleStrutPathD(el, conHeight, mX, mY) {
  const sx1 = mX(el.x1);
  const sy1 = mY(conHeight + el.y1);
  const sx2 = mX(el.x2);
  const sy2 = mY(conHeight + el.y2);
  const dx = sx2 - sx1;
  const dy = sy2 - sy1;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  const bulge = Math.min(18, len * 0.15);
  const steps = 12;
  const wEnd = 3;
  let d = '';
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const hw = wEnd + 4 * t * (1 - t) * bulge;
    const cx = sx1 + t * dx;
    const cy = sy1 + t * dy;
    d += i === 0 ? `M ${cx + nx * hw} ${cy + ny * hw}` : ` L ${cx + nx * hw} ${cy + ny * hw}`;
  }
  for (let i = steps; i >= 0; i -= 1) {
    const t = i / steps;
    const hw = wEnd + 4 * t * (1 - t) * bulge;
    const cx = sx1 + t * dx;
    const cy = sy1 + t * dy;
    d += ` L ${cx - nx * hw} ${cy - ny * hw}`;
  }
  return `${d} Z`;
}

const ConsoleResults = ({ calculation, results }) => {
  const [expanded, setExpanded] = useState('summary');
  const layoutSvgWrapRef = useRef(null);
  const stmSvgWrapRef = useRef(null);

  const handlePrintReport = useCallback(() => {
    const layoutSvg = layoutSvgWrapRef.current?.querySelector('svg');
    const stmSvg = stmSvgWrapRef.current?.querySelector('svg');
    openConsolePrintReport({
      calculation,
      results,
      layoutSvgHtml: layoutSvg?.outerHTML || '',
      stmSvgHtml: stmSvg?.outerHTML || '',
    });
  }, [calculation, results]);

  const handleExpansion = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const getStatusIcon = (isOk) => {
    if (isOk === undefined || isOk === null) return <InfoIcon color="info" />;
    return isOk ? <CheckCircleIcon color="success" /> : <ErrorIcon color="error" />;
  };

  // Dimensions & Data
  const colDepth = calculation.console_column_depth || 0.3;
  const colWidth = calculation.console_column_width || 0.3;
  const conHeight = calculation.console_height || 0.3;
  const tolerance = calculation.console_tolerance || 0.01;
  const minCover = calculation.console_minimum_cover || 0.035;
  // Bearing: neoprene pad (EN 1337-3) or direct concrete (EC2 §6.7); dimensions in mm → m
  const neoprenePadOn = calculation.console_neoprene_pad_enabled !== false;
  // Direct: bearing_length = structural contact L_eff; nominal strip for seat length on drawing.
  const contactDepthMm = neoprenePadOn
    ? (results.bearing_pad_analysis?.configuration?.depth ?? 150)
    : (results.direct_bearing_analysis?.bearing_length ?? 150);
  const bearingSeatLengthMm = neoprenePadOn
    ? contactDepthMm
    : (results.direct_bearing_analysis?.nominal_bearing_length_mm ?? contactDepthMm);
  const padDepth = contactDepthMm / 1000;
  const bearingSeatLength_m = bearingSeatLengthMm / 1000;
  const padWidth = (neoprenePadOn
    ? (results.bearing_pad_analysis?.configuration?.width ?? colWidth * 1000)
    : (results.direct_bearing_analysis?.bearing_width ?? colWidth * 1000)) / 1000;
  const beamDepth = calculation.console_beam_depth || 0.5;
  const beamLength = Math.max((neoprenePadOn ? padDepth : bearingSeatLength_m) * 1.5, 0.4);

  // Eccentricities (from mm to m)
  const eX = neoprenePadOn
    ? (results.bearing_pad_analysis?.eccentricity?.eccentricity_a || 0) / 1000
    : (results.direct_bearing_analysis?.eccentricity_x || 0) / 1000;
  const eY = (results.bearing_pad_analysis?.eccentricity?.eccentricity_b || 0) / 1000;

  // Reinforcement
  const mainRebar = results?.optimized_reinforcement?.main_reinforcement || results?.optimized_reinforcement?.main_rebar || {};
  const mainDiameterMm = mainRebar.diameter ? Math.round(mainRebar.diameter * 1000) : 16;
  const mainCount = mainRebar.count || 4;
  const mainAreaProvidedMm2 = mainCount * Math.PI * Math.pow(mainDiameterMm, 2) / 4;
  const fydMpa = results?.strut_tie_analysis?.material_properties?.fyd ?? 435;
  const requiredTieAreaMm2 = results?.strut_tie_analysis?.summary?.max_tie_force
    ? (results.strut_tie_analysis.summary.max_tie_force * 1000) / fydMpa
    : 0;
  const minRebarRequiredMm2 = colWidth * conHeight * 0.0013 * 1e6;

  const hStirrups = results?.optimized_reinforcement?.horizontal_stirrups || {};
  const hDiameterMm = hStirrups.diameter ? Math.round(hStirrups.diameter * 1000) : 8;
  const hAreaProvidedMm2 = hStirrups.area_provided ? hStirrups.area_provided * 1e6 : 0;

  const stm1 = results?.strut_tie_analysis?.visualization?.nodes?.find((n) => n.id === 1);
  const stmNode1X = stm1?.x;
  const stmNode1Y = stm1?.y;
  const dbAnalysis = results?.direct_bearing_analysis;

  const layout = useMemo(
    () =>
      computeConsoleLayoutMeters({
        neoprenePadOn,
        tolerance,
        padDepth,
        bearingSeatLength_m: neoprenePadOn ? undefined : bearingSeatLength_m,
        eX,
        conHeight,
        colDepth,
        minCover,
        mainDiameterMm,
        hDiameterMm,
        beamLength,
        db: dbAnalysis,
        stmNode1X,
        stmNode1Y,
      }),
    [
      neoprenePadOn,
      tolerance,
      padDepth,
      bearingSeatLength_m,
      eX,
      conHeight,
      colDepth,
      minCover,
      mainDiameterMm,
      hDiameterMm,
      beamLength,
      dbAnalysis,
      stmNode1X,
      stmNode1Y,
    ]
  );

  const hl = results?.strut_tie_analysis?.horizontal_links;
  const stmHorizontalRequiredMm2 = hl?.area_required ?? null;
  const stmHorizontalProvidedMm2 = hl?.area_provided ?? null;
  const horizontalLinkRequiredMm2 =
    stmHorizontalRequiredMm2 != null
      ? stmHorizontalRequiredMm2
      : 0.5 * Math.max(requiredTieAreaMm2, mainAreaProvidedMm2);
  const horizontalLinkProvidedMm2 =
    stmHorizontalProvidedMm2 != null ? stmHorizontalProvidedMm2 : hAreaProvidedMm2;
  const horizontalOk = horizontalLinkProvidedMm2 >= horizontalLinkRequiredMm2 - 0.01;
  const hlDiameterMm = hl?.link_diameter != null ? Math.round(hl.link_diameter * 1000) : hDiameterMm;
  const hlNumGroups = hl?.n_groups ?? hStirrups.num_groups ?? 0;

  // Coordinate Mapping for 2D View
  // We want to fill 90% of whichever dimension is smaller relative to content
  const svgWidth = 800;
  const svgHeight = 600;
  
  const colHeightAbove = beamDepth + 0.1;
  const colHeightBelow = conHeight / 3; 
  const totalH = conHeight + colHeightAbove + colHeightBelow;
  const totalW = colDepth + layout.L_corbel_m + 0.1;
  
  // Aspect ratio check
  const scale = Math.min((svgHeight * 0.9) / totalH, (svgWidth * 0.9) / totalW);
  
  // Center content
  const paddingX = (svgWidth - totalW * scale) / 2;
  const paddingY = (svgHeight - totalH * scale) / 2;

  const mX = (x) => paddingX + (x + colDepth) * scale; 
  const mY = (y) => svgHeight - paddingY - (y + colHeightBelow) * scale;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrintReport}>
          Print report (PDF)
        </Button>
      </Box>

      {/* Summary of console analysis */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom color="primary">Summary of console analysis</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">Status & Utilization</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Shear Utilization:</Typography>
                  <Typography fontWeight="bold">{results.console_utilization_shear?.toFixed(1)}%</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Moment Utilization:</Typography>
                  <Typography fontWeight="bold">{results.console_utilization_moment?.toFixed(1)}%</Typography>
                </Box>
                {(() => {
                  const dc = results.console_design_checks ?? {};
                  const stm = results.strut_tie_analysis;
                  const stmChk = stm?.checks ?? {};
                  const stmSum = stm?.summary ?? {};
                  const nodesOk = dc.stm_nodes_ok ?? stmChk.node_stress_ok ?? true;
                  const elemsOk =
                    dc.stm_elements_ok ??
                    ((stmChk.strut_capacity_ok ?? true) && (stmChk.tie_capacity_ok ?? true));
                  const nodeUrPct =
                    dc.stm_nodes_ur_pct ?? (stmSum.max_node_stress_ratio ?? 0) * 100;
                  const elemUrPct =
                    dc.stm_elements_ur_pct ?? (stmSum.max_element_utilization ?? 0) * 100;
                  return (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                        <Typography>STM nodal stress</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {!nodesOk && (
                            <Typography fontWeight="bold" color="error">
                              {nodeUrPct.toFixed(1)}%
                            </Typography>
                          )}
                          <Chip size="small" label={nodesOk ? 'OK' : 'FAIL'} color={nodesOk ? 'success' : 'error'} />
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                        <Typography>STM struts &amp; ties</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {!elemsOk && (
                            <Typography fontWeight="bold" color="error">
                              {elemUrPct.toFixed(1)}%
                            </Typography>
                          )}
                          <Chip size="small" label={elemsOk ? 'OK' : 'FAIL'} color={elemsOk ? 'success' : 'error'} />
                        </Box>
                      </Box>
                    </>
                  );
                })()}
                <Chip 
                  sx={{ mt: 1 }}
                  label={results.console_design_checks?.overall_ok ? "TOTAL PASS" : "DESIGN FAIL"} 
                  color={results.console_design_checks?.overall_ok ? "success" : "error"} 
                />
                {!results.console_design_checks?.overall_ok &&
                  Array.isArray(results.console_design_checks?.fail_reasons) &&
                  results.console_design_checks.fail_reasons.length > 0 && (
                    <Typography variant="caption" color="error" display="block" sx={{ mt: 0.75, lineHeight: 1.45 }}>
                      Governing gate(s):{' '}
                      {results.console_design_checks.fail_reasons
                        .map((code) => {
                          const m = {
                            strut_tie: 'STM (nodes or struts/ties)',
                            bearing_pad: 'Bearing pad module',
                            direct_bearing: 'Direct bearing module',
                            dowel: 'Dowel connection',
                            summary_shear_resistance_line:
                              'Summary shear line (horizontal load vs optimized stirrup capacity)',
                            summary_moment_resistance_line:
                              'Summary moment line (V×eccentricity vs tie capacity × lever arm proxy)',
                          };
                          return m[code] || code;
                        })
                        .join('; ')}
                      .
                    </Typography>
                  )}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">Calculated Capacities</Typography>
              <Typography sx={{ mt: 1 }}>Moment Resistance: <strong>{results.console_moment_capacity?.toFixed(1)} kNm</strong></Typography>
              <Typography>Shear Resistance: <strong>{results.console_shear_capacity?.toFixed(1)} kN</strong></Typography>
              <Typography>Main Reinforcement: <strong>{mainCount} × Ø{mainDiameterMm}</strong></Typography>
              <Typography>Rebar Ratio: <strong>{results.console_reinforcement_ratio?.toFixed(2)}%</strong></Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 2. Visualizations */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" align="center" gutterBottom>Detailed 2D Physical Layout</Typography>
            <Box ref={layoutSvgWrapRef} sx={{ width: '100%', position: 'relative', overflow: 'hidden' }}>
              <svg width="100%" height="auto" viewBox={`0 0 ${svgWidth} ${svgHeight}`} preserveAspectRatio="xMidYMid meet" style={{ display: 'block' }}>
                <g>
                  {/* Column */}
                  <rect 
                    x={mX(-colDepth)} 
                    y={mY(conHeight + colHeightAbove)} 
                    width={colDepth * scale} 
                    height={totalH * scale} 
                    fill="#eceff1" stroke="#1976d2" strokeWidth="2" 
                  />

                  {/* Console Geometry & Reinforcement (layout matches STM node 1 x and direct bearing tip) */}
                  <g>
                    <polygon
                      points={`
                        ${mX(0)},${mY(conHeight)}
                        ${mX(layout.L_corbel_m)},${mY(conHeight)}
                        ${mX(layout.L_corbel_m)},${mY(conHeight - layout.h_nose_concrete)}
                        ${mX(0)},${mY(0)}
                      `}
                      fill="#fff3e0"
                      stroke="#e65100"
                      strokeWidth="2"
                    />

                    <rect
                      x={mX(layout.bearingLeft)}
                      y={mY(conHeight + 0.015)}
                      width={(layout.bearingRight - layout.bearingLeft) * scale}
                      height={0.015 * scale}
                      fill="#263238"
                      stroke="#000"
                    />

                    <rect
                      x={mX(layout.beamLeft_m)}
                      y={mY(conHeight + 0.015 + beamDepth)}
                      width={layout.beamWidth_m * scale}
                      height={beamDepth * scale}
                      fill="#cfd8dc"
                      stroke="#455a64"
                      strokeWidth="1.5"
                    />

                    <g>
                      <line x1={mX(0)} y1={mY(-0.06)} x2={mX(layout.L_corbel_m)} y2={mY(-0.06)} stroke="#333" strokeWidth="1" />
                      <line x1={mX(0)} y1={mY(-0.07)} x2={mX(0)} y2={mY(-0.05)} stroke="#333" strokeWidth="1" />
                      <line x1={mX(layout.L_corbel_m)} y1={mY(-0.07)} x2={mX(layout.L_corbel_m)} y2={mY(-0.05)} stroke="#333" strokeWidth="1" />
                      <text x={mX(layout.L_corbel_m / 2)} y={mY(-0.08)} textAnchor="middle" fontSize="12" fill="#333" fontWeight="bold">
                        {neoprenePadOn
                          ? `L_total: ${(layout.L_corbel_m * 1000).toFixed(0)} mm`
                          : `L_seat: ${(layout.L_corbel_m * 1000).toFixed(0)} mm (corbel reach / nominal seat)`}
                      </text>
                    </g>

                    <path
                      d={`
                            M ${mX(layout.x_back + 0.05)} ${mY(layout.y_top_bar)}
                            L ${mX(layout.cx1)} ${mY(layout.y_top_bar)}
                            A ${layout.R_mandrel * scale} ${layout.R_mandrel * scale} 0 0 1 ${mX(layout.tx1)} ${mY(layout.ty1)}
                            L ${mX(layout.x_turn)} ${mY(layout.y_turn)}
                            L ${mX(layout.x_back + 0.05)} ${mY(layout.y_turn)}
                            Q ${mX(layout.x_back)} ${mY(layout.y_turn)} ${mX(layout.x_back)} ${mY(layout.y_turn + 0.05)}
                            L ${mX(layout.x_back)} ${mY(layout.y_top_bar - 0.05)}
                            Q ${mX(layout.x_back)} ${mY(layout.y_top_bar)} ${mX(layout.x_back + 0.05)} ${mY(layout.y_top_bar)}
                            Z
                          `}
                      fill="none"
                      stroke="#d32f2f"
                      strokeWidth={layout.d_m * scale}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />

                    {(() => {
                      const links = [];
                      const spacing = Math.min(hStirrups.spacing || 0.1, 0.1);
                      const d_col_m = calculation.console_column_main_rebar_diameter || 0.020;
                      const y_top_limit = layout.y_top_bar - layout.R_mandrel;
                      const numLinks = Math.floor((y_top_limit - 0.05) / spacing);
                      const { ty1, tx1, m_rebar, diag_offset, cx1, R_mandrel, d_m, d_s: tieD_s } = layout;

                      for (let i = 0; i <= numLinks; i += 1) {
                        const y = y_top_limit - i * spacing;
                        if (y < 0.05) break;
                        const x_intersect = (y - ty1) / m_rebar + tx1;
                        if (x_intersect > -colDepth + minCover) {
                          const startX = (-colDepth + minCover + d_col_m / 2) - (d_col_m / 2 + tieD_s);
                          const endX =
                            i === 0 ? cx1 + R_mandrel + d_m + tieD_s / 2 : x_intersect + diag_offset;
                          links.push(
                            <g key={i}>
                              <line
                                x1={mX(startX)}
                                y1={mY(y)}
                                x2={mX(endX)}
                                y2={mY(y)}
                                stroke="#1976d2"
                                strokeWidth={tieD_s * scale}
                                strokeDasharray="4,2"
                              />
                              <circle cx={mX(startX)} cy={mY(y)} r={(tieD_s / 2) * scale} fill="#1976d2" />
                              <circle cx={mX(endX)} cy={mY(y)} r={(tieD_s / 2) * scale} fill="#1976d2" />
                            </g>
                          );
                        }
                      }
                      return links;
                    })()}
                  </g>

                  {/* Column Main Bars */}
                  <line x1={mX(-colDepth + minCover)} y1={mY(conHeight + colHeightAbove)} x2={mX(-colDepth + minCover)} y2={mY(-colHeightBelow)} stroke="#b71c1c" strokeWidth="1" strokeDasharray="5,5" opacity="0.3" />
                  <line x1={mX(-minCover)} y1={mY(conHeight + colHeightAbove)} x2={mX(-minCover)} y2={mY(-colHeightBelow)} stroke="#b71c1c" strokeWidth="1" strokeDasharray="5,5" opacity="0.3" />

                  {/* x_c = column face → centroid of drawn structural contact (L_eff); STM resultant may differ under liftoff */}
                  {(() => {
                    const loadXDraw = layout.loadX_display_m ?? layout.loadX_m;
                    const loadXStm = layout.loadX_stm_m ?? layout.loadX_m;
                    const dimY = mY(conHeight + 0.015 + beamDepth + 0.05);
                    const divergeMm = Math.abs(loadXDraw - loadXStm) * 1000;
                    return (
                      <g>
                        <line x1={mX(0)} y1={dimY} x2={mX(loadXDraw)} y2={dimY} stroke="#1976d2" strokeWidth="1" />
                        <line x1={mX(0)} y1={dimY - 5} x2={mX(0)} y2={dimY + 5} stroke="#1976d2" strokeWidth="1" />
                        <line x1={mX(loadXDraw)} y1={dimY - 5} x2={mX(loadXDraw)} y2={dimY + 5} stroke="#1976d2" strokeWidth="1" />
                        <text x={mX(loadXDraw / 2)} y={dimY - 5} textAnchor="middle" fontSize="11" fill="#1976d2" fontWeight="bold">
                          {neoprenePadOn
                            ? `x_c: ${(loadXDraw * 1000).toFixed(0)} mm`
                            : `x_c: ${(loadXDraw * 1000).toFixed(0)} mm (mid L_eff strip)`}
                        </text>
                        {divergeMm > 0.5 && !neoprenePadOn ? (
                          <text x={mX(loadXDraw / 2)} y={dimY + 10} textAnchor="middle" fontSize="9" fill="#546e7a">
                            STM resultant (N1): {(loadXStm * 1000).toFixed(0)} mm
                          </text>
                        ) : null}
                        <line x1={mX(loadXDraw)} y1={mY(conHeight)} x2={mX(loadXDraw)} y2={mY(conHeight + 0.02)} stroke="#d32f2f" strokeWidth="2" />
                      </g>
                    );
                  })()}

                  {/* Loading Arrow */}
                  <defs>
                    <marker id="ptr" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto"><path d="M0,0 L0,6 L9,3 z" fill="#d32f2f"/></marker>
                  </defs>
                  <line x1={mX(layout.loadX_display_m ?? layout.loadX_m)} y1={mY(conHeight + 0.015 + beamDepth + 0.1)} x2={mX(layout.loadX_display_m ?? layout.loadX_m)} y2={mY(conHeight + 0.02)} stroke="#d32f2f" strokeWidth="3" markerEnd="url(#ptr)" />
                  <text x={mX(layout.loadX_display_m ?? layout.loadX_m)} y={mY(conHeight + 0.015 + beamDepth + 0.15)} textAnchor="middle" fontSize="14" fill="#d32f2f" fontWeight="bold">{calculation.console_vertical_load} kN</text>
                </g>
              </svg>
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1, px: 0.5, lineHeight: 1.45 }}>
                <strong>Red</strong> path: main tension tie; bend starts inboard of the <strong>STM pressure resultant</strong> (node N1) by mandrel radius + bar diameter, not at the corbel nose. <strong>Blue</strong>: horizontal links.{' '}
                {!neoprenePadOn && results.direct_bearing_analysis ? (
                  <>
                    Direct concrete: dark strip = structural contact length <em>L</em>
                    <sub>eff</sub>; grey beam block = nominal seat length. <em>x</em>
                    <sub>c</sub> on the diagram is the <strong>midpoint of the drawn <em>L</em>
                    <sub>eff</sub> strip</strong> (column face → centroid); STM N1 may sit elsewhere under liftoff / rotation.{' '}
                    With the Winkler beam model, a short <em>L</em>
                    <sub>eff</sub> patch is often at the <strong>column end</strong> of the seat (outer edge of the beam lifts); the vertical resultant is then <strong>not</strong> at the corbel nose.{' '}
                    Stiff packing / grout / neoprene is often needed if <em>L</em>
                    <sub>eff</sub> ≪ seat length.{' '}
                  </>
                ) : null}
                {neoprenePadOn ? (
                  <>
                    <em>L</em>
                    <sub>total</sub> includes the pad zone; N1-to-nose concrete is mainly compression/strut.{' '}
                  </>
                ) : (
                  <>
                    <em>L</em>
                    <sub>seat</sub> is corbel horizontal reach to the beam end (nominal seat); N1-to-nose concrete is mainly compression/strut.{' '}
                  </>
                )}
                Anchorage beyond the modelled tie is not shown.
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" align="center" gutterBottom>Strut-and-Tie Model</Typography>
            <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mb: 0.5 }}>
              Bottle struts (§6.5.2) are drawn with a mid-span bulge; fan-shaped struts stay prismatic.
            </Typography>
            <Box ref={stmSvgWrapRef} sx={{ width: '100%', position: 'relative', overflow: 'hidden' }}>
              <svg width="100%" height="auto" viewBox={`0 0 ${svgWidth} ${svgHeight}`} preserveAspectRatio="xMidYMid meet" style={{ display: 'block' }}>
                {(() => {
                  const vis = results.strut_tie_analysis?.visualization;
                  if (!vis) return <text x="400" y="300" textAnchor="middle">No STM Data</text>;
                  
                  return (
                    <g>
                      <g opacity="0.08" style={{ pointerEvents: 'none' }}>
                        <rect x={mX(-colDepth)} y={mY(conHeight + colHeightAbove)} width={colDepth * scale} height={totalH * scale} fill="#000" />
                        <polygon
                          points={`${mX(0)},${mY(conHeight)} ${mX(layout.L_corbel_m)},${mY(conHeight)} ${mX(layout.L_corbel_m)},${mY(conHeight - layout.h_nose_concrete)} ${mX(0)},${mY(0)}`}
                          fill="#000"
                        />
                        <rect
                          x={mX(layout.bearingLeft)}
                          y={mY(conHeight + 0.015)}
                          width={(layout.bearingRight - layout.bearingLeft) * scale}
                          height={0.015 * scale}
                          fill="#000"
                        />
                      </g>
                      
                      {vis.elements.map((el) => {
                        const strokeW = Math.max(3, Math.min(12, Math.abs(el.force) / 15));
                        const midX = (mX(el.x1) + mX(el.x2)) / 2;
                        const midY = (mY(conHeight + el.y1) + mY(conHeight + el.y2)) / 2;
                        const isBottle = el.type === 'strut' && el.strut_shape === 'bottle_shaped';
                        const strokeCol = el.color === 'blue' ? '#1976d2' : '#d32f2f';
                        return (
                          <g key={el.id}>
                            {isBottle ? (
                              <>
                                <path
                                  d={bottleStrutPathD(el, conHeight, mX, mY)}
                                  fill={strokeCol}
                                  fillOpacity={0.22}
                                  stroke={strokeCol}
                                  strokeWidth={2}
                                />
                                <line
                                  x1={mX(el.x1)}
                                  y1={mY(conHeight + el.y1)}
                                  x2={mX(el.x2)}
                                  y2={mY(conHeight + el.y2)}
                                  stroke={strokeCol}
                                  strokeWidth={1}
                                  strokeOpacity={0.5}
                                />
                              </>
                            ) : (
                              <line
                                x1={mX(el.x1)}
                                y1={mY(conHeight + el.y1)}
                                x2={mX(el.x2)}
                                y2={mY(conHeight + el.y2)}
                                stroke={strokeCol}
                                strokeWidth={strokeW}
                              />
                            )}
                            <text x={midX} y={midY - 12} textAnchor="middle" fontSize="12" fill={strokeCol} fontWeight="bold">
                              {Math.abs(el.force).toFixed(1)} kN
                            </text>
                          </g>
                        );
                      })}
                      
                      {vis.nodes.map(n => (
                        <g key={n.id}>
                          <circle cx={mX(n.x)} cy={mY(conHeight + n.y)} r="8" fill={n.type === 'CCC' ? '#1976d2' : '#d32f2f'} stroke="#fff" strokeWidth="2" />
                          <text x={mX(n.x)} y={mY(conHeight + n.y)-15} textAnchor="middle" fontSize="11" fontWeight="bold">N{n.id} ({n.type})</text>
                        </g>
                      ))}
                    </g>
                  );
                })()}
              </svg>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* 3. Rebar Verification */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom color="secondary">Reinforcement Verification (EN 1992-1-1)</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow sx={{ bgcolor: 'action.selected' }}>
              <TableCell>Check Component</TableCell><TableCell align="right">Required Area</TableCell><TableCell align="right">Provided Area</TableCell><TableCell align="center">Status</TableCell>
            </TableRow></TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <EurocodeText component="span">Main tension steel (A_s)</EurocodeText>
                  {' — strength & min.'}
                </TableCell>
                <TableCell align="right">{Math.max(requiredTieAreaMm2, minRebarRequiredMm2).toFixed(0)} mm²</TableCell>
                <TableCell align="right">
                  {mainAreaProvidedMm2.toFixed(0)} mm²
                  <Typography variant="caption" display="block" color="text.secondary">
                    ({mainCount}×Ø{mainDiameterMm}mm)
                  </Typography>
                </TableCell>
                <TableCell align="center">{getStatusIcon(mainAreaProvidedMm2 >= Math.max(requiredTieAreaMm2, minRebarRequiredMm2))}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Horizontal links (EC2 §6.5.4: ≥ 0.5×A_s,tie){' '}
                  {hlNumGroups > 0 ? `${hlNumGroups} loops (STM sizing)` : `${hStirrups.num_groups || 0} loops (detail)`}
                  {(hl?.link_spacing != null || hStirrups.spacing) && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      STM spacing @ {Math.round((hl?.link_spacing ?? hStirrups.spacing) * 1000)} mm c/c (upper 2/3){' '}
                      {hl?.source ? ` — ${hl.source}` : ''}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{horizontalLinkRequiredMm2.toFixed(0)} mm²</TableCell>
                <TableCell align="right">
                  {horizontalLinkProvidedMm2.toFixed(0)} mm²
                  <Typography variant="caption" display="block" color="text.secondary">
                    {hlNumGroups > 0
                      ? `(${hlNumGroups} × 2 × Ø${hlDiameterMm} mm, STM)`
                      : `(${hStirrups.num_groups || 0} × 2 × Ø${hDiameterMm} mm, detail)`}
                  </Typography>
                </TableCell>
                <TableCell align="center">{getStatusIcon(horizontalOk)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 4. Detailed Results Accordions */}
      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>Detailed Analysis Modules</Typography>
      
      <Accordion expanded={expanded === 'stm'} onChange={handleExpansion('stm')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight="bold">Strut-and-Tie Model Checks</Typography></AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle2" gutterBottom>Nodal Stress Verification</Typography>
          <TableContainer sx={{ mb: 3 }}><Table size="small">
            <TableHead><TableRow><TableCell>Node</TableCell><TableCell>Type</TableCell><TableCell align="right">Area (mm²)</TableCell><TableCell align="right">Stress (MPa)</TableCell><TableCell align="right">Limit (MPa)</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
            <TableBody>
              {Object.entries(results.strut_tie_analysis?.nodes || {}).map(([id, n]) => (
                <TableRow key={id}>
                  <TableCell>N{id}</TableCell><TableCell>{n.node_type}</TableCell>
                  <TableCell align="right">{n.area?.toFixed(0)}</TableCell><TableCell align="right">{n.sigma_max?.toFixed(1)}</TableCell>
                  <TableCell align="right">{n.allowable_stress?.toFixed(1)}</TableCell>
                  <TableCell><Chip size="small" label={n.capacity_ok ? "OK" : "FAIL"} color={n.capacity_ok ? "success" : "error"} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></TableContainer>

          <Typography variant="subtitle2" gutterBottom>Strut & Tie Capacity Verification</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Diagonal strut (E2): F<sub>Rd</sub> = σ<sub>Rd,max</sub> × w × b with w ≈ a₁ sin θ + a₃ cos θ (a₁ = bearing length along corbel,
            a₃ ≤ 50% of console height), b = console width. Compressive area = w × b.
          </Typography>
          <TableContainer><Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Element</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Strut model</TableCell>
                <TableCell align="right">w / w<sub>max</sub> (mm)</TableCell>
                <TableCell align="right">A = w×b (mm²)</TableCell>
                <TableCell align="right">Force (kN)</TableCell>
                <TableCell align="right">Capacity (kN)</TableCell>
                <TableCell align="right">Util.</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(results.strut_tie_analysis?.elements || {}).map(([id, e]) => (
                <TableRow key={id}>
                  <TableCell>E{id}</TableCell>
                  <TableCell>{e.element_type}</TableCell>
                  <TableCell>
                    {e.element_type === 'strut'
                      ? e.strut_type === 'bottle_shaped'
                        ? 'Bottle §6.5.2'
                        : 'Fan §6.5.2'
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {e.element_type === 'strut'
                      ? `${e.width?.toFixed(1) ?? '—'} / ${e.width_max?.toFixed(1) ?? '—'}`
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {e.element_type === 'strut' && e.compressive_area_mm2 != null
                      ? `${e.compressive_area_mm2.toFixed(0)}${e.geometry_limited ? ' (geom cap)' : ''}`
                      : '—'}
                  </TableCell>
                  <TableCell align="right">{Math.abs(e.force).toFixed(1)}</TableCell>
                  <TableCell align="right">{e.capacity?.toFixed(1)}</TableCell>
                  <TableCell align="right">{(e.utilization * 100).toFixed(1)}%</TableCell>
                  <TableCell><Chip size="small" label={e.capacity_ok ? "OK" : "FAIL"} color={e.capacity_ok ? "success" : "error"} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></TableContainer>
          {results.strut_tie_analysis?.elements?.[2]?.element_type === 'strut' && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              E2 geometry: a₁ ≈ {results.strut_tie_analysis.elements[2].a1_mm?.toFixed(0) ?? '—'} mm (bearing L), θ ≈{' '}
              {results.strut_tie_analysis.elements[2].theta_deg?.toFixed(1) ?? '—'}° (acute angle to horizontal for §6.5.2; raw atan2 ≈{' '}
              {results.strut_tie_analysis.elements[2].theta_raw_deg?.toFixed(1) ?? '—'}°), a₃ up to{' '}
              {results.strut_tie_analysis.elements[2].a3_max_mm?.toFixed(0) ?? '—'} mm. Plan width b = min(column, beam) when beam width is set.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={expanded === 'bearing'} onChange={handleExpansion('bearing')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight="bold">
            {neoprenePadOn ? 'Bearing Pad Analysis (EN 1337-3)' : 'Direct Bearing (EN 1992-1-1 §6.7)'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {neoprenePadOn && results.bearing_pad_analysis ? (
            <Grid container spacing={3}>
              {/* Configuration & Optimization Status */}
              <Grid item xs={12}>
                <Box sx={{ p: 1.5, bgcolor: results.bearing_pad_analysis.optimization?.optimized ? 'success.light' : 'info.light', borderRadius: 1, mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {results.bearing_pad_analysis.optimization?.message || 'Optimization status unknown'}
                  </Typography>
                </Box>
              </Grid>

              {/* Pad Configuration */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">Pad Configuration</Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Width (perpendicular to beam)</TableCell>
                      <TableCell align="right"><strong>{results.bearing_pad_analysis.configuration?.width?.toFixed(0)} mm</strong></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Depth (along beam axis)</TableCell>
                      <TableCell align="right"><strong>{results.bearing_pad_analysis.configuration?.depth?.toFixed(0)} mm</strong></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Neoprene Layers</TableCell>
                      <TableCell align="right">{results.bearing_pad_analysis.configuration?.neoprene_layers}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Layer Thickness (ti)</TableCell>
                      <TableCell align="right">{results.bearing_pad_analysis.configuration?.layer_thickness?.toFixed(0)} mm</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Steel Plate (ts)</TableCell>
                      <TableCell align="right">{results.bearing_pad_analysis.configuration?.steel_plate_thickness?.toFixed(0)} mm</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Total Thickness</TableCell>
                      <TableCell align="right"><strong>{results.bearing_pad_analysis.configuration?.total_thickness?.toFixed(0)} mm</strong></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Shape Factor (S)</TableCell>
                      <TableCell align="right">{results.bearing_pad_analysis.configuration?.shape_factor?.toFixed(2)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Grid>

              {/* Utilization Checks */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">EN 1337-3 Utilization Checks</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.selected' }}>
                      <TableCell>Check</TableCell>
                      <TableCell align="right">Utilization</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ bgcolor: results.bearing_pad_analysis.summary?.governing_check === 'compressive_stress' ? 'warning.light' : 'inherit' }}>
                      <TableCell>
                        Compressive Stress
                        <Typography variant="caption" display="block" color="text.secondary">
                          σc = {results.bearing_pad_analysis.stress_analysis?.compressive_stress?.toFixed(2)} / {results.bearing_pad_analysis.stress_analysis?.allowable_stress?.toFixed(2)} MPa
                        </Typography>
                      </TableCell>
                      <TableCell align="right"><strong>{((results.bearing_pad_analysis.stress_analysis?.stress_utilization || 0) * 100).toFixed(1)}%</strong></TableCell>
                      <TableCell align="center">{getStatusIcon((results.bearing_pad_analysis.stress_analysis?.stress_utilization || 0) <= 1.0)}</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: results.bearing_pad_analysis.summary?.governing_check === 'total_strain' ? 'warning.light' : 'inherit' }}>
                      <TableCell>
                        Total Strain (εt ≤ 5.0)
                        <Typography variant="caption" display="block" color="text.secondary">
                          εc + εq + εα = {results.bearing_pad_analysis.strain_analysis?.total_strain?.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right"><strong>{((results.bearing_pad_analysis.strain_analysis?.total_strain_utilization || 0) * 100).toFixed(1)}%</strong></TableCell>
                      <TableCell align="center">{getStatusIcon((results.bearing_pad_analysis.strain_analysis?.total_strain_utilization || 0) <= 1.0)}</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: results.bearing_pad_analysis.summary?.governing_check === 'shear_strain' ? 'warning.light' : 'inherit' }}>
                      <TableCell>
                        Shear Strain (εq ≤ 1.0)
                        <Typography variant="caption" display="block" color="text.secondary">
                          εq = {results.bearing_pad_analysis.strain_analysis?.shear_strain?.toFixed(3)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right"><strong>{((results.bearing_pad_analysis.strain_analysis?.shear_strain_utilization || 0) * 100).toFixed(1)}%</strong></TableCell>
                      <TableCell align="center">{getStatusIcon((results.bearing_pad_analysis.strain_analysis?.shear_strain_utilization || 0) <= 1.0)}</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: results.bearing_pad_analysis.summary?.governing_check === 'stability' ? 'warning.light' : 'inherit' }}>
                      <TableCell>
                        Stability
                        <Typography variant="caption" display="block" color="text.secondary">
                          σc·a'/(G·S²) = {results.bearing_pad_analysis.stability_analysis?.stability_ratio?.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right"><strong>{((results.bearing_pad_analysis.stability_analysis?.stability_utilization || 0) * 100).toFixed(1)}%</strong></TableCell>
                      <TableCell align="center">{getStatusIcon(results.bearing_pad_analysis.stability_analysis?.stability_ok)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        Steel Plate Thickness
                        <Typography variant="caption" display="block" color="text.secondary">
                          ts,req = {results.bearing_pad_analysis.steel_plate_analysis?.required_thickness?.toFixed(1)} mm
                        </Typography>
                      </TableCell>
                      <TableCell align="right">—</TableCell>
                      <TableCell align="center">{getStatusIcon(results.bearing_pad_analysis.steel_plate_analysis?.thickness_ok)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Box sx={{ mt: 2 }}>
                  <Chip 
                    label={results.bearing_pad_analysis.summary?.all_checks_pass ? "ALL CHECKS PASS" : "DESIGN FAIL"} 
                    color={results.bearing_pad_analysis.summary?.all_checks_pass ? "success" : "error"} 
                    sx={{ fontWeight: 'bold' }}
                  />
                  {results.bearing_pad_analysis.summary?.governing_check && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                      Governing: {results.bearing_pad_analysis.summary.governing_check.replace(/_/g, ' ')} ({((results.bearing_pad_analysis.summary?.max_utilization || 0) * 100).toFixed(1)}%)
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Strain Breakdown & Eccentricity */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">Strain Components</Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Compressive Strain (εc)</TableCell>
                      <TableCell align="right">{results.bearing_pad_analysis.strain_analysis?.compressive_strain?.toFixed(3)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Shear Strain (εq)</TableCell>
                      <TableCell align="right">{results.bearing_pad_analysis.strain_analysis?.shear_strain?.toFixed(3)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Rotational Strain (εα)</TableCell>
                      <TableCell align="right">{results.bearing_pad_analysis.strain_analysis?.rotational_strain?.toFixed(3)}</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: 'action.selected' }}>
                      <TableCell><strong>Total Strain (εt)</strong></TableCell>
                      <TableCell align="right"><strong>{results.bearing_pad_analysis.strain_analysis?.total_strain?.toFixed(3)}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mt: 2 }}>Eccentricity & Effective Area</Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Eccentricity ea (depth dir)</TableCell>
                      <TableCell align="right">{results.bearing_pad_analysis.eccentricity?.eccentricity_a?.toFixed(1)} mm</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Eccentricity eb (width dir)</TableCell>
                      <TableCell align="right">{results.bearing_pad_analysis.eccentricity?.eccentricity_b?.toFixed(1)} mm</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Effective Area (A')</TableCell>
                      <TableCell align="right">{(results.bearing_pad_analysis.eccentricity?.effective_area * 1e6)?.toFixed(0)} mm²</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Max Pressure</TableCell>
                      <TableCell align="right"><strong>{results.bearing_pad_analysis.eccentricity?.max_pressure?.toFixed(2)} MPa</strong></TableCell>
                    </TableRow>
                    {(results.bearing_pad_analysis.eccentricity?.lift_off_depth || results.bearing_pad_analysis.eccentricity?.lift_off_width) && (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Chip size="small" label="Lift-off detected" color="warning" />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Grid>

              {/* 2D Pressure Distribution Mesh */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  2D Pressure Distribution (8×8 Mesh) - Hover for Values
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                      {(() => {
                        const grid = results.bearing_pad_analysis.pressure_distribution?.pressure_mpa || [];
                        if (!grid.length) return <Typography color="text.secondary">No pressure data available</Typography>;
                        
                        const flat = grid.flat();
                        const maxP = Math.max(...flat, 0.1);
                        const size = grid.length;
                        const cellSize = 350 / size;
                        
                        // Load centroid position on mesh
                        const meshPadDepth = results.bearing_pad_analysis.pressure_distribution?.pad_depth || 0.15;
                        const meshPadWidth = results.bearing_pad_analysis.pressure_distribution?.pad_width || 0.3;
                        const meshEccX = results.bearing_pad_analysis.pressure_distribution?.eccentricity_x || 0;
                        const meshEccY = results.bearing_pad_analysis.pressure_distribution?.eccentricity_y || 0;
                        
                        // Eccentricity (m) from pad centre; mesh columns i = depth along beam, rows j = width b
                        const plot = 350;
                        const ml = 48;
                        const mt = 10;
                        const mb = 36;
                        const mr = 12;
                        const cX_plot = plot / 2 + (meshEccX / meshPadDepth) * plot;
                        const cY_plot = plot / 2 + (meshEccY / meshPadWidth) * plot;

                        return (
                          <svg
                            width={ml + plot + mr}
                            height={mt + plot + mb}
                            style={{ border: '1px solid #ddd', borderRadius: '4px' }}
                          >
                            <g transform={`translate(${ml},${mt})`}>
                              {grid.map((row, j) => row.map((val, i) => (
                                <Tooltip
                                  key={`${j}-${i}`}
                                  title={`X (along beam) cell ${i + 1}, Y (width b) cell ${j + 1}: ${val.toFixed(3)} MPa`}
                                  arrow
                                >
                                  <rect
                                    x={i * cellSize}
                                    y={j * cellSize}
                                    width={cellSize}
                                    height={cellSize}
                                    fill={val > 0.001 ? `rgba(220, 50, 50, ${Math.min(val / maxP, 1)})` : '#f5f5f5'}
                                    stroke="#fff"
                                    strokeWidth="1"
                                    style={{ cursor: 'pointer' }}
                                  />
                                </Tooltip>
                              )))}
                              <g transform={`translate(${cX_plot},${cY_plot})`}>
                                <circle r="12" fill="none" stroke="#1976d2" strokeWidth="2" />
                                <line x1="-8" y1="0" x2="8" y2="0" stroke="#1976d2" strokeWidth="2" />
                                <line x1="0" y1="-8" x2="0" y2="8" stroke="#1976d2" strokeWidth="2" />
                              </g>
                              {/* Plot frame */}
                              <rect x="0" y="0" width={plot} height={plot} fill="none" stroke="#bbb" strokeWidth="1" />
                            </g>
                            <text
                              x={ml + plot / 2}
                              y={mt + plot + 26}
                              textAnchor="middle"
                              fontSize="11"
                              fill="#333"
                            >
                              X — beam axis / pad depth L → (toward column)
                            </text>
                            <text
                              x={14}
                              y={mt + plot / 2}
                              textAnchor="middle"
                              fontSize="11"
                              fill="#333"
                              transform={`rotate(-90, 14, ${mt + plot / 2})`}
                            >
                              Y — beam width b (column depth direction)
                            </text>
                          </svg>
                        );
                      })()}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center', maxWidth: 440, mx: 'auto' }}>
                      Axes: X along beam (pad depth L), Y across beam (pad width b). Crosshair = pressure resultant from mesh.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Pressure Distribution Details</Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Maximum Pressure</TableCell>
                          <TableCell align="right"><strong>{results.bearing_pad_analysis.pressure_distribution?.max_pressure?.toFixed(3)} MPa</strong></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Minimum Pressure</TableCell>
                          <TableCell align="right">{results.bearing_pad_analysis.pressure_distribution?.min_pressure?.toFixed(3)} MPa</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Average Pressure</TableCell>
                          <TableCell align="right">{results.bearing_pad_analysis.pressure_distribution?.avg_pressure?.toFixed(3)} MPa</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Active Elements</TableCell>
                          <TableCell align="right">{results.bearing_pad_analysis.pressure_distribution?.active_elements} / {results.bearing_pad_analysis.pressure_distribution?.total_elements}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Effective Depth</TableCell>
                          <TableCell align="right">{(results.bearing_pad_analysis.pressure_distribution?.effective_depth * 1000)?.toFixed(0)} mm</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Effective Width</TableCell>
                          <TableCell align="right">{(results.bearing_pad_analysis.pressure_distribution?.effective_width * 1000)?.toFixed(0)} mm</TableCell>
                        </TableRow>
                        {results.bearing_pad_analysis.pressure_distribution?.lift_off && (
                          <TableRow>
                            <TableCell colSpan={2}>
                              <Chip size="small" label="Partial lift-off detected" color="warning" />
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                    <Box sx={{ mt: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        <strong>Legend:</strong> Darker red = higher pressure. 
                        Blue crosshair = load resultant centroid.
                        Gray cells = zero pressure (lift-off).
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              {/* Input Summary */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Inputs: Fz = {results.bearing_pad_analysis.inputs?.vertical_load} kN, 
                  Fx = {results.bearing_pad_analysis.inputs?.horizontal_load} kN, 
                  Rotation = {results.bearing_pad_analysis.inputs?.beam_rotation}°, 
                  Shore {results.bearing_pad_analysis.inputs?.shore_hardness}, 
                  T = {results.bearing_pad_analysis.inputs?.temperature}°C
                  {results.bearing_pad_analysis.inputs?.dowel_count > 0 && `, ${results.bearing_pad_analysis.inputs.dowel_count} dowels Ø${results.bearing_pad_analysis.inputs.dowel_diameter}mm`}
                </Typography>
              </Grid>
            </Grid>
          ) : !neoprenePadOn && results.direct_bearing_analysis ? (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: results.direct_bearing_analysis.optimization?.no_valid_configuration
                      ? 'error.light'
                      : results.direct_bearing_analysis.optimization?.optimized
                        ? 'success.light'
                        : 'warning.light',
                    borderRadius: 1,
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    {results.direct_bearing_analysis.optimization?.message || 'Direct bearing'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">Geometry</Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow><TableCell>Bearing contact length (structural L_eff)</TableCell><TableCell align="right"><strong>{results.direct_bearing_analysis.bearing_length?.toFixed(0)} mm</strong></TableCell></TableRow>
                    {results.direct_bearing_analysis.geometry_fallback_for_layout && (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography variant="caption" color="text.secondary">
                            No valid direct contact: seat length and 2D layout use indicative minimum from L_req (not the failed FE trial). See optimization message for corbel reach breakdown.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {results.direct_bearing_analysis.nominal_bearing_length_mm != null
                      && Math.abs(results.direct_bearing_analysis.nominal_bearing_length_mm - (results.direct_bearing_analysis.bearing_length || 0)) > 0.5 && (
                      <TableRow><TableCell>Nominal seat / STM strut length</TableCell><TableCell align="right">{results.direct_bearing_analysis.nominal_bearing_length_mm.toFixed(0)} mm</TableCell></TableRow>
                    )}
                    {results.direct_bearing_analysis.geometry_fallback_for_layout && results.direct_bearing_analysis.corbel_horizontal_m != null && (
                      <TableRow>
                        <TableCell>Indicative corbel horizontal (layout)</TableCell>
                        <TableCell align="right"><strong>{(results.direct_bearing_analysis.corbel_horizontal_m * 1000).toFixed(0)} mm</strong></TableCell>
                      </TableRow>
                    )}
                    <TableRow><TableCell>Bearing width b</TableCell><TableCell align="right">{results.direct_bearing_analysis.bearing_width?.toFixed(0)} mm</TableCell></TableRow>
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography variant="caption" color="text.secondary">
                          b = min(column plan width, beam width) when beam width is set — not corbel depth (300 mm column ≠ 300 mm “depth” in side view).
                          {results.direct_bearing_analysis.inputs?.column_plan_width_m != null && (
                            <> Column plan: {(results.direct_bearing_analysis.inputs.column_plan_width_m * 1000).toFixed(0)} mm
                            {results.direct_bearing_analysis.inputs.beam_width_m != null
                              ? `, beam: ${(results.direct_bearing_analysis.inputs.beam_width_m * 1000).toFixed(0)} mm`
                              : ''}.
                            </>
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow><TableCell>Contact area A_c0 (structural)</TableCell><TableCell align="right">{results.direct_bearing_analysis.bearing_area?.toFixed(0)} mm²</TableCell></TableRow>
                    <TableRow>
                      <TableCell>Distribution area A_c1</TableCell>
                      <TableCell align="right">{results.direct_bearing_analysis.distribution_area?.toFixed(0)} mm²</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography variant="caption" color="text.secondary">
                          A_c1 = min(geometry, 9×A<sub>c0</sub>) with geometry = min(<em>b</em>×(<em>L</em>+spread),{' '}
                          <em>W</em>×(<em>L</em>+<em>W</em>−<em>b</em>)), spread = min(corbel depth, 2<em>L</em>), <em>W</em> = column plan width.
                          EC2 clause 6.7: √(A<sub>c1</sub>/A<sub>c0</sub>) ≤ 3 (from <em>f</em>
                          <sub>cd</sub>√(·) ≤ 3<em>f</em>
                          <sub>cd</sub>), so area ratio ≤ 9.
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow><TableCell>Cantilever arm (STM)</TableCell><TableCell align="right">{(results.direct_bearing_analysis.cantilever_arm * 1000)?.toFixed(0)} mm</TableCell></TableRow>
                    <TableRow><TableCell>EC2 §10.9.5 min length</TableCell><TableCell align="right">{results.direct_bearing_analysis.min_bearing_per_ec2?.toFixed(0)} mm</TableCell></TableRow>
                  </TableBody>
                </Table>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">Stresses & checks</Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow><TableCell>Peak stress σ</TableCell><TableCell align="right">{results.direct_bearing_analysis.bearing_stress?.toFixed(2)} MPa</TableCell></TableRow>
                    <TableRow><TableCell>Mean stress</TableCell><TableCell align="right">{results.direct_bearing_analysis.bearing_stress_mean?.toFixed(2)} MPa</TableCell></TableRow>
                    <TableRow><TableCell>Allowable (§6.7)</TableCell><TableCell align="right">{results.direct_bearing_analysis.allowable_stress?.toFixed(2)} MPa</TableCell></TableRow>
                    <TableRow><TableCell>F_Rdu</TableCell><TableCell align="right">{results.direct_bearing_analysis.F_Rdu_kn?.toFixed(1)} kN</TableCell></TableRow>
                    <TableRow><TableCell>Utilization V/F_Rdu</TableCell><TableCell align="right"><strong>{((results.direct_bearing_analysis.utilization || 0) * 100).toFixed(1)}%</strong></TableCell></TableRow>
                    <TableRow><TableCell>STM N1 σ (combined on A_eff)</TableCell><TableCell align="right">{results.direct_bearing_analysis.stm_n1_stress_mpa != null ? `${results.direct_bearing_analysis.stm_n1_stress_mpa.toFixed(2)} MPa` : '—'}</TableCell></TableRow>
                    <TableRow><TableCell>STM N1 allowable (§6.5.4 CCC)</TableCell><TableCell align="right">{results.direct_bearing_analysis.stm_n1_allowable_mpa != null ? `${results.direct_bearing_analysis.stm_n1_allowable_mpa.toFixed(2)} MPa` : '—'}</TableCell></TableRow>
                    <TableRow><TableCell>STM N1 utilization</TableCell><TableCell align="right"><strong>{results.direct_bearing_analysis.stm_n1_utilization != null ? `${(results.direct_bearing_analysis.stm_n1_utilization * 100).toFixed(1)}%` : '—'}</strong></TableCell></TableRow>
                    <TableRow>
                      <TableCell>e_x (resultant vs pad centre, + toward column)</TableCell>
                      <TableCell align="right">{results.direct_bearing_analysis.eccentricity_x?.toFixed(1)} mm</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>e_x STM (pad frame, same as neoprene)</TableCell>
                      <TableCell align="right">
                        {results.direct_bearing_analysis.pressure_distribution?.eccentricity_x != null
                          ? `${(results.direct_bearing_analysis.pressure_distribution.eccentricity_x * 1000).toFixed(1)} mm`
                          : results.direct_bearing_analysis.e_x_stm_m != null
                            ? `${(results.direct_bearing_analysis.e_x_stm_m * 1000).toFixed(1)} mm`
                            : '—'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography variant="caption" color="text.secondary">
                          Layout and STM N1 use cantilever_arm from the bearing model when available (direct bearing).
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow><TableCell>Overall</TableCell><TableCell align="right">{getStatusIcon(results.direct_bearing_analysis.overall_check)}</TableCell></TableRow>
                  </TableBody>
                </Table>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">Rotation (Winkler elastic–plastic)</Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow><TableCell>Beam rotation θ</TableCell><TableCell align="right">{results.direct_bearing_analysis.rotation_effects?.alpha_deg?.toFixed(2)}°</TableCell></TableRow>
                    <TableRow>
                      <TableCell>Contact regime</TableCell>
                      <TableCell align="right">
                        {(() => {
                          const r = results.direct_bearing_analysis.rotation_effects?.regime;
                          if (r === 'elastic_full') return 'Elastic full contact';
                          if (r === 'elastic_liftoff') return 'Elastic liftoff (triangular)';
                          if (r === 'plastic') return 'Plastic (uniform f_cd block)';
                          return r != null ? String(r) : '—';
                        })()}
                      </TableCell>
                    </TableRow>
                    <TableRow><TableCell>θ_crit (liftoff onset)</TableCell><TableCell align="right">{results.direct_bearing_analysis.rotation_effects?.theta_crit_deg != null ? `${results.direct_bearing_analysis.rotation_effects.theta_crit_deg.toFixed(3)}°` : '—'}</TableCell></TableRow>
                    <TableRow><TableCell>Area contact (L_eff·B_eff)/(L·b)</TableCell><TableCell align="right">{(results.direct_bearing_analysis.rotation_effects?.contact_ratio * 100)?.toFixed(1)}%</TableCell></TableRow>
                    <TableRow><TableCell>L_eff / L (nominal)</TableCell><TableCell align="right">{results.direct_bearing_analysis.rotation_effects?.L_eff_over_L_nominal != null ? `${(results.direct_bearing_analysis.rotation_effects.L_eff_over_L_nominal * 100).toFixed(1)}%` : '—'}</TableCell></TableRow>
                    <TableRow><TableCell>B_eff / b (nominal)</TableCell><TableCell align="right">{results.direct_bearing_analysis.rotation_effects?.B_eff_over_b_nominal != null ? `${(results.direct_bearing_analysis.rotation_effects.B_eff_over_b_nominal * 100).toFixed(1)}%` : '—'}</TableCell></TableRow>
                    <TableRow><TableCell>Peak σ / uniform on L·b</TableCell><TableCell align="right">{results.direct_bearing_analysis.rotation_effects?.peak_to_uniform_nominal_ratio != null && results.direct_bearing_analysis.rotation_effects.peak_to_uniform_nominal_ratio > 0 ? results.direct_bearing_analysis.rotation_effects.peak_to_uniform_nominal_ratio.toFixed(2) : '—'}</TableCell></TableRow>
                    <TableRow><TableCell>Load distribution OK</TableCell><TableCell align="right">{getStatusIcon(results.direct_bearing_analysis.rotation_effects?.distribution_feasible)}</TableCell></TableRow>
                    <TableRow><TableCell>σ_min (outer edge)</TableCell><TableCell align="right">{results.direct_bearing_analysis.rotation_effects?.sigma_min_mpa != null ? `${results.direct_bearing_analysis.rotation_effects.sigma_min_mpa.toFixed(2)} MPa` : '—'}</TableCell></TableRow>
                    <TableRow><TableCell>k_w (Winkler)</TableCell><TableCell align="right">{results.direct_bearing_analysis.rotation_effects?.k_w_mpa_per_m != null ? `${results.direct_bearing_analysis.rotation_effects.k_w_mpa_per_m.toFixed(0)} MPa/m` : '—'}</TableCell></TableRow>
                    <TableRow><TableCell>Lift-off</TableCell><TableCell align="right">{results.direct_bearing_analysis.rotation_effects?.lift_off ? 'Yes' : 'No'}</TableCell></TableRow>
                  </TableBody>
                </Table>
              </Grid>
              {results.direct_bearing_analysis.pressure_distribution?.pressure_mpa?.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    Effective bearing map (Winkler strip, uniform across width b)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                        {(() => {
                          const pd = results.direct_bearing_analysis.pressure_distribution;
                          const grid = pd?.pressure_mpa || [];
                          if (!grid.length) return null;
                          const flat = grid.flat();
                          const maxP = Math.max(...flat, 0.1);
                          const size = grid.length;
                          const cellSize = 350 / size;
                          const meshPadDepth = pd.pad_depth || 0.15;
                          const meshPadWidth = pd.pad_width || 0.3;
                          const meshEccY = pd.eccentricity_y || 0;
                          // Backend grid rows = along bearing length L, cols = across width b — map X=L horizontal, Y=b vertical
                          const xResM =
                            results.direct_bearing_analysis.x_resultant_from_outer_mm != null
                              ? results.direct_bearing_analysis.x_resultant_from_outer_mm / 1000
                              : meshPadDepth / 2 - (pd.eccentricity_x || 0);
                          const plot = 350;
                          const ml = 48;
                          const mt = 10;
                          const mb = 36;
                          const mr = 12;
                          const cX_plot = (xResM / meshPadDepth) * plot;
                          const cY_plot = plot / 2 + (meshEccY / meshPadWidth) * plot;
                          return (
                            <svg
                              width={ml + plot + mr}
                              height={mt + plot + mb}
                              style={{ border: '1px solid #ddd', borderRadius: '4px' }}
                            >
                              <g transform={`translate(${ml},${mt})`}>
                                {grid.map((row, depthIdx) =>
                                  row.map((val, widthIdx) => (
                                    <Tooltip
                                      key={`db-${depthIdx}-${widthIdx}`}
                                      title={`X (along beam) cell ${depthIdx + 1}, Y (width b) cell ${widthIdx + 1}: ${val.toFixed(3)} MPa`}
                                      arrow
                                    >
                                      <rect
                                        x={depthIdx * cellSize}
                                        y={widthIdx * cellSize}
                                        width={cellSize}
                                        height={cellSize}
                                        fill={val > 0.001 ? `rgba(220, 50, 50, ${Math.min(val / maxP, 1)})` : '#f5f5f5'}
                                        stroke="#fff"
                                        strokeWidth="1"
                                        style={{ cursor: 'pointer' }}
                                      />
                                    </Tooltip>
                                  ))
                                )}
                                <g transform={`translate(${cX_plot},${cY_plot})`}>
                                  <circle r="12" fill="none" stroke="#1976d2" strokeWidth="2" />
                                  <line x1="-8" y1="0" x2="8" y2="0" stroke="#1976d2" strokeWidth="2" />
                                  <line x1="0" y1="-8" x2="0" y2="8" stroke="#1976d2" strokeWidth="2" />
                                </g>
                                <rect x="0" y="0" width={plot} height={plot} fill="none" stroke="#bbb" strokeWidth="1" />
                              </g>
                              <text
                                x={ml + plot / 2}
                                y={mt + plot + 26}
                                textAnchor="middle"
                                fontSize="11"
                                fill="#333"
                              >
                                X — beam axis / bearing length L → (toward column)
                              </text>
                              <text
                                x={14}
                                y={mt + plot / 2}
                                textAnchor="middle"
                                fontSize="11"
                                fill="#333"
                                transform={`rotate(-90, 14, ${mt + plot / 2})`}
                              >
                                Y — beam width b (column depth direction)
                              </text>
                            </svg>
                          );
                        })()}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center', maxWidth: 440, mx: 'auto' }}>
                        Blue crosshair: vertical resultant position along X measured from the outer edge (beam end); Y is at mid-width when contact is uniform across b.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Strip model details</Typography>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell>Maximum pressure</TableCell>
                            <TableCell align="right"><strong>{results.direct_bearing_analysis.pressure_distribution?.max_pressure?.toFixed(3)} MPa</strong></TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Minimum pressure</TableCell>
                            <TableCell align="right">{results.direct_bearing_analysis.pressure_distribution?.min_pressure?.toFixed(3)} MPa</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Average (on A_eff)</TableCell>
                            <TableCell align="right">{results.direct_bearing_analysis.pressure_distribution?.avg_pressure?.toFixed(3)} MPa</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Active cells</TableCell>
                            <TableCell align="right">
                              {results.direct_bearing_analysis.pressure_distribution?.active_elements} /{' '}
                              {results.direct_bearing_analysis.pressure_distribution?.total_elements}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>L_eff / B_eff</TableCell>
                            <TableCell align="right">
                              {(results.direct_bearing_analysis.pressure_distribution?.effective_depth * 1000)?.toFixed(0)} /{' '}
                              {(results.direct_bearing_analysis.pressure_distribution?.effective_width * 1000)?.toFixed(0)} mm
                            </TableCell>
                          </TableRow>
                          {results.direct_bearing_analysis.pressure_distribution?.lift_off && (
                            <TableRow>
                              <TableCell colSpan={2}>
                                <Chip size="small" label="Partial lift-off (strip)" color="warning" />
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Rows duplicate the same along-beam σ(x); blue crosshair uses e_x in the pad-centred frame (matches STM N1).
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          ) : (
            <Box p={2}>
              <Typography color="error">
                {neoprenePadOn
                  ? 'Bearing pad analysis failed. Check module errors below.'
                  : 'Direct bearing analysis failed. Check module errors below.'}
              </Typography>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={expanded === 'dowel'} onChange={handleExpansion('dowel')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight="bold">Dowel Connection & Shear Transfer</Typography></AccordionSummary>
        <AccordionDetails>
          {results.dowel_analysis ? (
            <Box>
              <Table size="small">
                <TableBody>
                  <TableRow><TableCell>Total Shear Capacity (Dowel + Friction)</TableCell><TableCell align="right"><strong>{calculation.console_dowel_shear_capacity?.toFixed(1)} kN</strong></TableCell></TableRow>
                  <TableRow><TableCell>Lever Arm (e_d)</TableCell><TableCell align="right"><strong>{(calculation.console_dowel_lever_arm * 1000).toFixed(0)} mm</strong></TableCell></TableRow>
                </TableBody>
              </Table>
              {results.dowel_analysis.capacities?.vertical_load_model && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Vertical load path: {results.dowel_analysis.capacities.vertical_load_model.vertical_reaction_path}.
                  Dowel roles: {(results.dowel_analysis.capacities.vertical_load_model.dowel_roles || []).join(', ')}.
                  {results.dowel_analysis.capacities.vertical_load_model.not_checked_here && (
                    <> {results.dowel_analysis.capacities.vertical_load_model.not_checked_here}</>
                  )}
                </Typography>
              )}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Shear Participation Rates</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip label={`Friction: ${results.dowel_analysis.participation?.friction_percent?.toFixed(1)}%`} color="primary" variant="outlined" />
                <Chip label={`Dowels: ${results.dowel_analysis.participation?.dowel_percent?.toFixed(1)}%`} color="secondary" variant="outlined" />
              </Box>
            </Box>
          ) : <Typography color="error">Dowel analysis failed or missing.</Typography>}
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={expanded === 'column'} onChange={handleExpansion('column')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight="bold">Column Anchorage Verification</Typography></AccordionSummary>
        <AccordionDetails>
          {results.column_verification ? (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2, lineHeight: 1.45 }}>
                STM main tie and horizontal-link checks assess the corbel model. This block adds (1) anchorage length along the column
                and (2) a separate simplified check of shear, bending and bearing on the column at the console interface — not the same
                as strut or tie element OK in the STM table.
              </Typography>
              <Typography>Available Embedment: <strong>{(results.column_verification.embedment_check?.available_embedment * 1000).toFixed(0)} mm</strong></Typography>
              <Typography>Required Straight Anchorage: <strong>{(results.column_verification.embedment_check?.required_embedment_straight * 1000).toFixed(0)} mm</strong></Typography>
              <Typography>Required Bent Anchorage: <strong>{(results.column_verification.embedment_check?.required_embedment_bent * 1000).toFixed(0)} mm</strong></Typography>
              {(() => {
                const cv = results.column_verification;
                const emb = cv.embedment_check;
                const embOk = emb?.sufficient === true;
                const ch = cv.capacity_check?.checks;
                const capFail = ch && ch.overall_ok === false;
                const congFail = cv.congestion_check && cv.congestion_check.congestion_ok === false;
                const overall = cv.overall_ok;
                let chipLabel = 'COLUMN CHECK OK';
                let chipColor = 'success';
                if (!overall) {
                  const parts = [];
                  if (!embOk) parts.push('embedment');
                  if (capFail) {
                    const u = cv.capacity_check?.utilization ?? {};
                    const iface = [];
                    if (ch && !ch.shear_ok) iface.push(`shear ${(u.shear * 100).toFixed(0)}%`);
                    if (ch && !ch.moment_ok) iface.push(`moment ${(u.moment * 100).toFixed(0)}%`);
                    if (ch && !ch.bearing_ok) iface.push(`bearing ${(u.bearing * 100).toFixed(0)}%`);
                    parts.push(iface.length ? `interface (${iface.join(', ')})` : 'interface capacity');
                  }
                  if (congFail) parts.push('congestion');
                  chipLabel = parts.length ? `FAIL: ${parts.join('; ')}` : 'FAIL';
                  chipColor = 'error';
                }
                return <Chip sx={{ mt: 2 }} label={chipLabel} color={chipColor} size="small" />;
              })()}
              {results.column_verification.capacity_check && (
                <TableContainer sx={{ mt: 2, maxWidth: 520 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, lineHeight: 1.45 }}>
                    <strong>Shear (interface):</strong> demand{' '}
                    <em>V</em>
                    <sub>d</sub> = √(<em>F</em>
                    <sub>x</sub>² + (|<em>M</em>|/<em>b</em>)²) kN, with <em>M</em> = <em>F</em>
                    <sub>x</sub> × console depth (fixed-end couple from corbel) and <em>b</em> = column plan width. Resistance: EN 1992-1-1 clause 6.2,{' '}
                    <em>V</em>
                    <sub>Rd,c</sub> + <em>V</em>
                    <sub>Rd,s</sub> (column links as two legs). Simplified column model — not STM corbel shear.
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell>Column interface check</TableCell>
                        <TableCell align="right">Utilization</TableCell>
                        <TableCell align="center">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const cc = results.column_verification.capacity_check;
                        const u = cc.utilization || {};
                        const c = cc.checks || {};
                        const row = (label, key) => (
                          <TableRow key={key}>
                            <TableCell>{label}</TableCell>
                            <TableCell align="right">{((u[key] ?? 0) * 100).toFixed(1)}%</TableCell>
                            <TableCell align="center">
                              <Chip size="small" label={c[`${key}_ok`] ? 'OK' : 'FAIL'} color={c[`${key}_ok`] ? 'success' : 'error'} />
                            </TableCell>
                          </TableRow>
                        );
                        const cong = results.column_verification.congestion_check;
                        return (
                          <>
                            {row('Shear (horizontal + moment transfer)', 'shear')}
                            {row('Moment (main bars, simplified)', 'moment')}
                            {row('Bearing stress on console face', 'bearing')}
                            {cong && (
                              <TableRow>
                                <TableCell>Bar spacing / congestion</TableCell>
                                <TableCell align="right">—</TableCell>
                                <TableCell align="center">
                                  <Chip
                                    size="small"
                                    label={cong.congestion_ok ? 'OK' : 'FAIL'}
                                    color={cong.congestion_ok ? 'success' : 'error'}
                                  />
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {results.column_verification.embedment_check && !results.column_verification.embedment_check.sufficient && (
                <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                  Embedment deficiency: {(results.column_verification.embedment_check.deficiency * 1000).toFixed(0)} mm
                </Typography>
              )}
              {results.column_verification.overall_ok === false && results.column_verification.embedment_check?.sufficient && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Embedment is sufficient; any remaining fail is from the interface table above or bar congestion — not anchorage depth.
                </Typography>
              )}
            </Box>
          ) : <Typography color="error">Column verification failed.</Typography>}
        </AccordionDetails>
      </Accordion>

      {/* Module Error Debugging */}
      {(results.analysis_errors?.bearing_pad || results.analysis_errors?.direct_bearing || results.analysis_errors?.dowel) && (
        <Paper sx={{ p: 2, mt: 4, bgcolor: '#fff3f3', border: '1px solid #fcc' }}>
          <Typography color="error" fontWeight="bold">Module Diagnostics:</Typography>
          {results.analysis_errors.bearing_pad && <Typography variant="caption" display="block">• Bearing Pad: {results.analysis_errors.bearing_pad}</Typography>}
          {results.analysis_errors.direct_bearing && <Typography variant="caption" display="block">• Direct Bearing: {results.analysis_errors.direct_bearing}</Typography>}
          {results.analysis_errors.dowel && <Typography variant="caption" display="block">• Dowel: {results.analysis_errors.dowel}</Typography>}
        </Paper>
      )}
    </Box>
  );
};

export default ConsoleResults;
